import { useCallback, useState } from 'react'

import {
    IrrigationScheduleModeForm,
    modeLabel,
} from './irrigation-schedule-mode-form'
import type {
    IrrigationDevice,
    IrrigationScheduleView,
} from '@/lib/api/irrigation-devices-api'
import type { IrrigationScheduleFormValues } from '@/lib/schemas/irrigation-schemas'
import type { DraftBasis } from './irrigation-schedule-mode-form'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { scheduleSummary } from '@/lib/irrigation-schedule'
import { m } from '@/lib/paraglide/messages'

type Mode = 'fixed' | 'interval'

interface Draft {
    basis: DraftBasis
    values: IrrigationScheduleFormValues
}

interface Props {
    device: IrrigationDevice
    saved: IrrigationScheduleView | null | undefined
    canEdit: boolean
}

/**
 * Lịch tưới của một bộ: mỗi loại (giờ cố định / chu kỳ) giữ cấu hình riêng,
 * chỉ một loại được áp dụng. Tab chỉ chọn loại để xem/sửa; áp dụng hay không
 * là công tắc trong từng loại.
 */
export function IrrigationScheduleForm({ device, saved, canEdit }: Props) {
    const activeMode: Mode | null =
        saved?.mode === 'fixed' || saved?.mode === 'interval'
            ? saved.mode
            : null
    const [tab, setTab] = useState<Mode>(activeMode ?? 'fixed')
    const configs = saved?.configs
    const tabSaved = tab === 'fixed' ? configs?.fixed : configs?.interval
    const isActive = activeMode === tab
    const otherActive = activeMode !== null && activeMode !== tab
    // Dữ liệu server mà form của tab dựng lên từ đó. Cấu hình đã lưu đổi thì
    // bỏ phần sửa dở; chỉ trạng thái áp dụng đổi thì giữ phần sửa dở nhưng
    // nạp lại công tắc "Áp dụng lịch này".
    const basis = {
        config: JSON.stringify(tabSaved ?? null),
        status: JSON.stringify([isActive, otherActive]),
    }
    // Phần đang sửa dở của từng loại, để chuyển tab qua lại không mất.
    const [drafts, setDrafts] = useState<Partial<Record<Mode, Draft>>>({})
    const keepDraft = useCallback(
        (mode: Mode, draft: Draft) =>
            setDrafts((d) => ({ ...d, [mode]: draft })),
        [],
    )
    const draft = drafts[tab]

    return (
        <div className="space-y-4">
            <p className="rounded-md border px-3 py-2 text-sm">
                {activeMode && saved?.config
                    ? m.irrigation_schedule_status_active({
                          mode: modeLabel(activeMode),
                          summary: scheduleSummary(saved.config),
                      })
                    : m.irrigation_schedule_status_none()}
            </p>
            <Tabs value={tab} onValueChange={(v) => setTab(v as Mode)}>
                <TabsList className="w-full">
                    {(['fixed', 'interval'] as const).map((value) => (
                        <TabsTrigger
                            key={value}
                            value={value}
                            className="flex-1 gap-1.5"
                        >
                            {modeLabel(value)}
                            {activeMode === value && (
                                <Badge variant="secondary">
                                    {m.irrigation_schedule_active_badge()}
                                </Badge>
                            )}
                        </TabsTrigger>
                    ))}
                </TabsList>
            </Tabs>
            {/* key theo loại + dữ liệu server: đổi tab thì nạp phần đang sửa
                dở của loại đó (nếu vẫn dựa trên dữ liệu hiện tại), lưu xong
                thì nạp lại đúng cấu hình đã lưu. */}
            <IrrigationScheduleModeForm
                key={`${tab}-${basis.config}-${basis.status}`}
                basis={basis}
                draft={
                    draft?.basis.config === basis.config
                        ? {
                              values: draft.values,
                              keepActive: draft.basis.status === basis.status,
                          }
                        : undefined
                }
                onDraft={keepDraft}
                device={device}
                mode={tab}
                saved={tabSaved}
                isActive={isActive}
                otherActive={otherActive}
                canEdit={canEdit}
            />
        </div>
    )
}
